/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form, OverlayTrigger, Popover } from 'react-bootstrap'
import { Formik, yupToFormErrors } from 'formik'
// @ts-expect-error - react-color type compatibility
import { ChromePicker } from 'react-color'
import ValidationSchema from './EditFormValidation'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import { Select } from '../../../../components/Select'
import Knob from '../../../../components/Knob'
import WatermarkPreview from '../WatermarkPreview'
// import SystemKeywordsPopover from '../../../components/SystemKeywordsPopover'
import { getAvailableTypes } from '../constants'
import { transformToSelectOptions, formatTextAreaCounter } from '../utils'
import TextArea from '@mdsol/lego/lib/TextArea'
import { useHelpPage } from '../../../../services/helpPage'
import Field from '../../../../components/shared/Field'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'
import Modal from '../../../../components/Modal'
import HtmlCell from '~/components/shared/HtmlCell'

const HalfWidthDiv = styled.div`
  width: 50%;
  padding-right: 23px;
  float: left;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  watermark: any
  loading: Promise<any> | null
}

const EditForm: React.FC<EditFormProps> = ({ onSubmit, doClose, isEdit, watermark, loading }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()
  const helpPage = useHelpPage()

  const [horizontalAlignment, setHorizontalAlignment] = useState<any[]>([])
  const [textAlignment, setTextAlignment] = useState<any[]>([])
  const [verticalAlignment, setVerticalAlignment] = useState<any[]>([])
  const [fontStyle, setFontStyle] = useState<any[]>([])
  const [fontSize, setFontSize] = useState<any[]>([])

  const getDefaults = (vals: any) => {
    const defaults = validationSchema.cast(vals, {
      recursive: true,
      stripUnknown: true,
      context: vals
    })
    return defaults
  }

  const [initialValues, setInitialValues] = useState(() => getDefaults(watermark))

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    // Update initial values when watermark data is loaded
    if (isEdit && watermark && Object.keys(watermark).length > 0) {
      setInitialValues(getDefaults(watermark))
    }
  }, [watermark, isEdit])

  const loadData = () => {
    setHorizontalAlignment(transformToSelectOptions(getAvailableTypes('horizontalAlignment', translate)))
    setTextAlignment(transformToSelectOptions(getAvailableTypes('textAlignment', translate)))
    setVerticalAlignment(transformToSelectOptions(getAvailableTypes('verticalAlignment', translate)))
    setFontStyle(transformToSelectOptions(getAvailableTypes('fontStyle', translate)))
    setFontSize(transformToSelectOptions(getAvailableTypes('fontSize', translate)))
  }

  const closeDialog = () => {
    if (doClose) {
      doClose()
    }
  }

  return (
    <div>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => yupToFormErrors(err)
            )
        }}
        onSubmit={(values, actions) => {
          return onSubmit(values, actions)
        }}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <div>
            <Loader blocking={(isEdit && Object.keys(watermark).length === 0) || loading !== null}>
              <Modal.Body>
                <form className={'clearfix'}>
                  <HalfWidthDiv>
                    <StyledFormGroup controlId="3e9732a9-5ca4-471a-8c08-0cdcc44a6420" className={'w100p'}>
                      <Form.Label>{translate('NAME')}</Form.Label>
                      <Field
                        type="text"
                        name="name"
                        maxLength={255}
                        autoFocus
                        id="3e9732a9-5ca4-471a-8c08-0cdcc44a6420"
                      />
                      <ErrorMessageStyled name="name" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="712a53f3-eb12-485a-9f74-f1fc518714a1" className={'w100p'}>
                      <Form.Label>{translate('DESCRIPTION')}</Form.Label>
                      <Field type="text" name="description" maxLength={255} id="712a53f3-eb12-485a-9f74-f1fc518714a1" />
                      <ErrorMessageStyled name="description" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="f4733cc1-f2d7-4ef2-ab7f-67e8804d24d3" className={'w100p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_WATERMARK_TEXT')}</Form.Label>

                      <div className="text-area-help-block">
                        <OverlayTrigger
                          trigger="click"
                          placement="top"
                          rootClose={true}
                          overlay={
                            <Popover id={'6fe7eae7-350e-4339-b487-bf956b7723f5'}>
                              <>
                                <button
                                  data-action={'ok'}
                                  type="button"
                                  className="close"
                                  onClick={() => document.body.click()}
                                >
                                  <span aria-hidden="true">×</span>
                                </button>
                                <HtmlCell style={{ paddingTop: '6px' }}>
                                  {translate('WATERMARKS_POPOVER_HELP_TXT') +
                                    '<br><br>' +
                                    translate('WATERMARKS_POPOVER_HELP_LINK', {
                                      url: 'https://learn.medidata.com/en-US/bundle/rcm-stg/page/system_keywords.html?lang=en'
                                    })}
                                </HtmlCell>
                              </>
                            </Popover>
                          }
                        >
                          <TextArea
                            {...({
                              name: 'stampText',
                              id: 'f4733cc1-f2d7-4ef2-ab7f-67e8804d24d3',
                              className: 'form-control',
                              counterClassName: 'fs12 pull-right text-muted',
                              maxLength: 255,
                              rows: 6,
                              value: values.stampText || '',
                              onChange: (e: any) => setFieldValue('stampText', e.target.value),
                              formatCounter: (length: number, max: number) =>
                                formatTextAreaCounter(length, max, translate('CHAR_COUNT_LIMIT'))
                            } as any)}
                          />
                        </OverlayTrigger>
                      </div>

                      <ErrorMessageStyled name="stampText" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="d38304a7-6a72-44d4-8780-3ddcb4dab4a3" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_TEXT_ALIGNMENT')}</Form.Label>
                      <Select
                        name="textAlignment"
                        value={values.textAlignment}
                        options={textAlignment}
                        isClearable={false}
                        onChange={(option: any) => {
                          setFieldValue('textAlignment', option ? option.value : null)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        id="d38304a7-6a72-44d4-8780-3ddcb4dab4a3"
                      />
                      <ErrorMessageStyled name="textAlignment" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="3d4becd7-2a91-4734-b1b1-cb66ffd71197" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_VERTICAL_ALIGNMENT')}</Form.Label>
                      <Select
                        name="verticalAlignment"
                        value={values.verticalAlignment}
                        options={verticalAlignment}
                        isClearable={false}
                        onChange={(option: any) => {
                          setFieldValue('verticalAlignment', option ? option.value : null)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        id="3d4becd7-2a91-4734-b1b1-cb66ffd71197"
                      />
                      <ErrorMessageStyled name="verticalAlignment" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="3565febc-c32d-4b85-8597-a1811a3cd894" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_HORIZONTAL_ALIGNMENT')}</Form.Label>
                      <Select
                        name="horizontalAlignment"
                        value={values.horizontalAlignment}
                        options={horizontalAlignment}
                        isClearable={false}
                        onChange={(option: any) => {
                          setFieldValue('horizontalAlignment', option ? option.value : null)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        id="3565febc-c32d-4b85-8597-a1811a3cd894"
                      />
                      <ErrorMessageStyled name="horizontalAlignment" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="26a345d6-b035-42b3-b277-d5bb6870e69b" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_ROTATE_ANGLE')}</Form.Label>

                      <Knob
                        value={values.rotateAngle}
                        id={'26a345d6-b035-42b3-b277-d5bb6870e69b'}
                        name="rotateAngle"
                        onChange={(e: any) => setFieldValue('rotateAngle', e)}
                      />

                      <ErrorMessageStyled name="rotateAngle" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="bbaed366-e1a8-4ab1-9028-1a0f5a168c47" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_TRANSPARENCY')}</Form.Label>
                      <div className="slidecontainer">
                        <input
                          name="opacity"
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          id={'bbaed366-e1a8-4ab1-9028-1a0f5a168c47'}
                          style={{ direction: 'rtl' }}
                          onChange={(e) => setFieldValue('opacity', e.target.value)}
                          value={values.opacity}
                          className="slider"
                        />
                        <span className="sliderlbl-minval">0%</span>
                        <span className="sliderlbl-maxval">100%</span>
                      </div>
                      <ErrorMessageStyled name="opacity" />
                    </StyledFormGroup>
                  </HalfWidthDiv>

                  <HalfWidthDiv>
                    <div id={'9b660e4c-5629-448a-8d8b-16beec467514'}>
                      <StyledFormGroup className={'w100p'}>
                        <Form.Label>{translate('WATERMARKS_FORM_LABEL_FOREGROUND_COLOR')}</Form.Label>
                        <ChromePicker
                          color={values.foregroundColor}
                          name={'foregroundColor'}
                          onChange={(e: any) => {
                            setFieldValue('foregroundColor', e.hex)
                          }}
                          disableAlpha={true}
                        />
                        <ErrorMessageStyled name="foregroundColor" />
                      </StyledFormGroup>
                    </div>

                    <StyledFormGroup controlId="c29b9010-ebfa-40f0-a8bd-0a8e5667d962" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_FONT_STYLE')}</Form.Label>
                      <Select
                        name="fontStyle"
                        value={values.fontStyle}
                        options={fontStyle}
                        isClearable={false}
                        onChange={(option: any) => {
                          setFieldValue('fontStyle', option ? option.value : null)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        id="c29b9010-ebfa-40f0-a8bd-0a8e5667d962"
                      />
                      <ErrorMessageStyled name="fontStyle" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="d4a0c6f5-9c18-48f3-8732-c192c3c5db7c" className={'w80p'}>
                      <Form.Label>{translate('WATERMARKS_FORM_LABEL_FONT_SIZE')}</Form.Label>
                      <Select
                        name="fontSize"
                        value={values.fontSize}
                        options={fontSize}
                        isClearable={false}
                        onChange={(option: any) => {
                          setFieldValue('fontSize', option ? option.value : null)
                        }}
                        showNewOptionAtTop={true}
                        searchable
                        id="d4a0c6f5-9c18-48f3-8732-c192c3c5db7c"
                      />
                      <ErrorMessageStyled name="fontSize" />
                    </StyledFormGroup>

                    <StyledFormGroup>
                      <Form.Label>{translate('WATERMARKS_PREVIEW')}</Form.Label>
                      <WatermarkPreview data={values} />
                    </StyledFormGroup>
                  </HalfWidthDiv>
                </form>
              </Modal.Body>
              <Modal.Footer>
                <button
                  data-action={'cancel'}
                  className="btn btn-chita btn-link"
                  onClick={closeDialog}
                  id="61c55a7c-1458-4ffa-95d8-4fc3b71eb135"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  data-action={'save'}
                  className="btn btn-primary text-capitalize"
                  onClick={() => handleSubmit()}
                  id="1eced302-0423-4ff2-ba4a-8740f13d794e"
                >
                  {translate('SAVE')}
                </button>
              </Modal.Footer>
            </Loader>
          </div>
        )}
      </Formik>
    </div>
  )
}

export default EditForm
